/* Camera photos on the device: straighten, cap the size and re-encode before
   anything leaves the scanner, so a packing-list page is a few hundred KB on
   the warehouse Wi-Fi instead of a 4 MB camera original. */
export async function loadBitmap(file) {
  // browsers honour the camera's EXIF orientation here, so the page is upright;
  // older Safari gets the same through a plain <img>, which it also rotates
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      return img;
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

const sizeOf = (src) => ({ w: src.naturalWidth || src.width, h: src.naturalHeight || src.height });

export async function shrinkPhoto(file, maxSide = 1800, quality = 0.85) {
  const bmp = await loadBitmap(file);
  const { w, h } = sizeOf(bmp);
  const scale = Math.min(1, maxSide / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
  bmp.close?.();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  return { blob, url: URL.createObjectURL(blob), width: canvas.width, height: canvas.height };
}

export async function saveOrShare(blob, name, title) {
  const file = new File([blob], name, { type: blob.type });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title });
      return 'shared';
    } catch (err) {
      if (err?.name === 'AbortError') return 'cancelled';
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return 'saved';
}
